/**
 * Stockout Analysis Report Component
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ReportResponse, StockoutAnalysisItem, StockoutSeverity } from '../../types/reports';

const StockoutAnalysisReport: React.FC = () => {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [selectedStore, setSelectedStore] = useState('');

  // Fetch stores
  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data;
    },
  });

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportResponse<StockoutAnalysisItem>>({
    queryKey: ['stockoutAnalysisReport', startDate, endDate, selectedStore],
    queryFn: async () => {
      const response = await api.getStockoutAnalysisReport({
        start_date: startDate,
        end_date: endDate,
        store_id: selectedStore || undefined,
      });
      return response.data;
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response =
        format === 'csv'
          ? await api.exportReportCSV('stockout_analysis', {
              start_date: startDate,
              end_date: endDate,
              store_id: selectedStore || undefined,
            })
          : await api.exportReportJSON('stockout_analysis', {
              start_date: startDate,
              end_date: endDate,
              store_id: selectedStore || undefined,
            });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stockout_analysis_${startDate}_${endDate}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Export error - silently handled
    }
  };

  const getSeverityColor = (
    severity: string
  ): 'error' | 'warning' | 'default' => {
    switch (severity) {
      case StockoutSeverity.CRITICAL:
        return 'error';
      case StockoutSeverity.MODERATE:
        return 'warning';
      default:
        return 'default';
    }
  };

  // Calculate total lost sales
  const totalLostSales = reportData?.data.reduce(
    (sum, item) => sum + item.estimated_lost_sales_units,
    0
  ) || 0;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Store Filter</InputLabel>
            <Select
              value={selectedStore}
              label="Store Filter"
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores?.map((store: any) => (
                <MenuItem key={store.store_id} value={store.store_id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
            >
              Export
            </Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading report: {(error as Error).message}
        </Alert>
      )}

      {reportData && (
        <>
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Stockouts Detected: {reportData.metadata.record_count}</strong>
            </Typography>
            <Typography variant="body2">
              Estimated Total Lost Sales: <strong>{totalLostSales.toFixed(0)} units</strong>
            </Typography>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(reportData.metadata.generated_at).toLocaleString()}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Item Status</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">In Transit</TableCell>
                  <TableCell align="right">Avg Daily Sales</TableCell>
                  <TableCell align="right">Days Out of Stock</TableCell>
                  <TableCell align="right">Est. Lost Sales</TableCell>
                  <TableCell>Stock Status</TableCell>
                  <TableCell>Recommended Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <Alert severity="success">
                        No stockouts detected - Great job!
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.data.map((item: StockoutAnalysisItem, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={item.stockout_severity}
                          color={getSeverityColor(item.stockout_severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.store_name}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(item as any).lifecycle_stage ? (
                          <Chip
                            label={(item as any).lifecycle_stage}
                            size="small"
                            color={
                              (item as any).lifecycle_stage === 'NEW' ? 'info' :
                              (item as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (item as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((item as any).lifecycle_stage === 'PHASE_OUT' || (item as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{item.current_stock}</TableCell>
                      <TableCell align="right">{item.in_transit}</TableCell>
                      <TableCell align="right">
                        {Math.ceil(Number(item.avg_daily_sales) || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.days_out_of_stock}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {item.estimated_lost_sales_units.toFixed(0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${
                          item.status === 'RED' ? 'status-badge--danger' :
                          item.status === 'YELLOW' ? 'status-badge--warning' : 'status-badge--success'
                        }`}>
                          {item.status || 'RED'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {item.recommended_action.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default StockoutAnalysisReport;
